// SoilDataForm.tsx

import React from 'react';
import {useForm, Controller} from 'react-hook-form';
import {
  Box,
  Button,
  Chip,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import './SoilDataForm.scss';

// Soil types you want to display as selectable chips
const SOIL_TYPES = ['Clay', 'Loamy', 'Sandy', 'Peat', 'Chalky'];

/**
 * The shape of your soilData object:
 */
export interface ISoilData {
  soilType?: string;
  ph?: number;
  P?: number; // Phosphorus
  K?: number; // Potassium
  Ca?: number;
  Mg?: number;
  Na?: number;
  cations?: number;
  exchangeableAcidity?: number;
}

interface SoilDataFormProps {
  initialSoilData?: ISoilData;
  onSubmit: (data: ISoilData) => void;
  onCancel?: () => void;
  readOnly?: boolean; // if true, display in view-only mode (no editing, no validation)
}

// Validation helper functions
const getPhosphorusWarning = (
  value: number | string | undefined,
): string | null => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (numValue === undefined || numValue === null || isNaN(numValue))
    return null;
  if (numValue < 5) return 'Warning: Very Low';
  if (numValue > 150) return 'Warning: Unusually High (Check Units?)';
  return null;
};

const getPotassiumWarning = (
  value: number | string | undefined,
): string | null => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (numValue === undefined || numValue === null || isNaN(numValue))
    return null;
  if (numValue < 40) return 'Warning: Critically Low';
  if (numValue > 600) return 'Warning: Unusually High';
  return null;
};

const getMagnesiumWarning = (
  value: number | string | undefined,
): string | null => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (numValue === undefined || numValue === null || isNaN(numValue))
    return null;
  if (numValue > 1000) return 'Warning: Unusually High';
  return null;
};

const getCalciumWarning = (
  value: number | string | undefined,
): string | null => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (numValue === undefined || numValue === null || isNaN(numValue))
    return null;
  if (numValue > 6000)
    return 'Warning: This value seems extremely high. Did you mean ppm?';
  return null;
};

const validatePH = (value: number | string | undefined): true | string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (numValue === undefined || numValue === null || isNaN(numValue))
    return true;
  if (numValue < 3 || numValue > 10) {
    return 'Soil pH must be between 3 and 10. Please check your input.';
  }
  return true;
};

export function SoilDataForm(props: SoilDataFormProps) {
  const {onSubmit, initialSoilData, readOnly = false} = props;

  // Debug log to verify readOnly is being passed
  React.useEffect(() => {
    console.log('SoilDataForm - readOnly:', readOnly);
  }, [readOnly]);
  const {handleSubmit, control, reset, watch} = useForm<ISoilData>({
    defaultValues: {
      soilType: initialSoilData?.soilType || '',
      ph: initialSoilData?.ph,
      P: initialSoilData?.P,
      K: initialSoilData?.K,
      Ca: initialSoilData?.Ca,
      Mg: initialSoilData?.Mg,
      Na: initialSoilData?.Na,
      cations: initialSoilData?.cations,
      exchangeableAcidity: initialSoilData?.exchangeableAcidity,
    },
  });

  const selectedSoilType = watch('soilType');

  // Handler for resetting all fields
  const handleClearAll = () => {
    reset({
      soilType: '',
      ph: undefined,
      P: undefined,
      K: undefined,
      Ca: undefined,
      Mg: undefined,
      Na: undefined,
      cations: undefined,
      exchangeableAcidity: undefined,
    });
  };

  return (
    <div className="soil-data-form-container">
      <Typography variant="h6" className="title">
        {readOnly ? 'Soil Data Results' : 'Input soil data'}
      </Typography>

      {/*
        We separate the scrolling region (the fields) from the fixed bottom actions.
        This allows the fields to scroll, but the buttons remain visible.
      */}
      <form
        onSubmit={readOnly ? undefined : handleSubmit(onSubmit)}
        className="soil-data-form"
      >
        <div className="scrollable-section">
          <Box className="soil-type-row">
            <Typography variant="subtitle2" className="sub-heading">
              Soil type
            </Typography>

            <Box className="chip-wrapper">
              {SOIL_TYPES.map((type) => (
                <Controller
                  key={type}
                  name="soilType"
                  control={control}
                  render={({field: {onChange}}) => (
                    <Chip
                      label={type}
                      clickable={!readOnly}
                      onClick={readOnly ? undefined : () => onChange(type)}
                      className={
                        selectedSoilType === type
                          ? 'soil-chip active'
                          : 'soil-chip'
                      }
                      style={readOnly ? {cursor: 'default'} : {}}
                    />
                  )}
                />
              ))}
            </Box>
          </Box>

          <Box className="main-fields">
            <Typography variant="subtitle2" className="section-heading">
              Primary nutrients
            </Typography>
            <Grid container spacing={2}>
              {/* Phosphorus (P) */}
              <Grid item xs={12}>
                <Controller
                  name="P"
                  control={control}
                  render={({field}) => {
                    const warning = readOnly
                      ? null
                      : getPhosphorusWarning(field.value);
                    return (
                      <TextField
                        {...field}
                        type="number"
                        label="Phosphorus levels (P) (mg/kg)"
                        variant="outlined"
                        fullWidth
                        placeholder="Enter quantity"
                        disabled={readOnly}
                        helperText={
                          readOnly
                            ? undefined
                            : warning || 'Ideal range: 10-100 mg/kg'
                        }
                        error={false}
                        FormHelperTextProps={{
                          style: {
                            color: warning ? '#ff9800' : 'rgba(0, 0, 0, 0.54)',
                          },
                        }}
                      />
                    );
                  }}
                />
              </Grid>

              {/* Potassium (K) */}
              <Grid item xs={12}>
                <Controller
                  name="K"
                  control={control}
                  render={({field}) => {
                    const warning = readOnly
                      ? null
                      : getPotassiumWarning(field.value);
                    return (
                      <TextField
                        {...field}
                        type="number"
                        label="Potassium levels (K) (mg/kg)"
                        variant="outlined"
                        fullWidth
                        placeholder="Enter quantity"
                        disabled={readOnly}
                        helperText={
                          readOnly
                            ? undefined
                            : warning || 'Ideal range: 80-300 mg/kg'
                        }
                        error={false}
                        FormHelperTextProps={{
                          style: {
                            color: warning ? '#ff9800' : 'rgba(0, 0, 0, 0.54)',
                          },
                        }}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              {/* Magnesium (mg) */}
              <Grid item xs={12}>
                <Controller
                  name="Mg"
                  control={control}
                  render={({field}) => {
                    const warning = readOnly
                      ? null
                      : getMagnesiumWarning(field.value);
                    return (
                      <TextField
                        {...field}
                        type="number"
                        label="Magnesium (Mg) (mg/kg)"
                        variant="outlined"
                        fullWidth
                        placeholder="Enter quantity"
                        disabled={readOnly}
                        helperText={
                          readOnly
                            ? undefined
                            : warning || 'Ideal range: 100-500 mg/kg'
                        }
                        error={false}
                        FormHelperTextProps={{
                          style: {
                            color: warning ? '#ff9800' : 'rgba(0, 0, 0, 0.54)',
                          },
                        }}
                      />
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="Ca"
                  control={control}
                  render={({field}) => {
                    const warning = readOnly
                      ? null
                      : getCalciumWarning(field.value);
                    return (
                      <TextField
                        {...field}
                        type="number"
                        label="Calcium (Ca) (mg/kg)"
                        variant="outlined"
                        fullWidth
                        placeholder="Enter quantity"
                        disabled={readOnly}
                        helperText={
                          readOnly
                            ? undefined
                            : warning || 'Ideal range: 400-3000 mg/kg'
                        }
                        error={false}
                        FormHelperTextProps={{
                          style: {
                            color: warning ? '#ff9800' : 'rgba(0, 0, 0, 0.54)',
                          },
                        }}
                      />
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="Na"
                  control={control}
                  render={({field}) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Sodium (Na) (mg/kg)"
                      variant="outlined"
                      fullWidth
                      placeholder="Enter quantity"
                      disabled={readOnly}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="cations"
                  control={control}
                  render={({field}) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Cations (cmol/kg)"
                      variant="outlined"
                      fullWidth
                      placeholder="Enter quantity"
                      disabled={readOnly}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="exchangeableAcidity"
                  control={control}
                  render={({field}) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Exchangeable acidity (cmol/kg)"
                      variant="outlined"
                      fullWidth
                      placeholder="Enter quantity"
                      disabled={readOnly}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="ph"
                  control={control}
                  rules={readOnly ? {} : {validate: validatePH}}
                  render={({field, fieldState: {error}}) => (
                    <TextField
                      {...field}
                      type="number"
                      label="pH"
                      variant="outlined"
                      fullWidth
                      placeholder="Enter pH"
                      disabled={readOnly}
                      helperText={
                        readOnly
                          ? undefined
                          : error?.message || 'Ideal range: 4.5-8.5'
                      }
                      error={readOnly ? false : !!error}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </div>

        {!readOnly && (
          <div className="bottom-actions">
            <Button onClick={handleClearAll} className="clear-all-btn">
              Clear All
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              className="submit-btn"
            >
              Add
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
